import React, { useRef } from 'react';
import { useReducedMotion } from 'framer-motion';
import { cn } from '../../lib/utils';
import { useTypewriterPlaceholder } from '../../hooks/useTypewriterPlaceholder';

// ═══════════════════════════════════════════════════════════════════════════════
// CHAT PLACEHOLDER OVERLAY
//
// Sits on top of the empty composer and types example prompts built from the
// user's real files and tasks, so the reference syntax is discoverable rather
// than documented.
//
// It is an overlay, not the input's own placeholder, because a CSS pseudo-
// element cannot render the styled `@file` chips that make the examples worth
// showing. Pointer events pass straight through to the input beneath.
// ═══════════════════════════════════════════════════════════════════════════════

const SEGMENT_COUNT = 3;

const chipClass = {
    file: 'mx-0.5 rounded-md border border-purple-500/20 bg-purple-500/10 px-1.5 text-purple-300',
    task: 'mx-0.5 rounded-md border border-green-500/20 bg-green-500/10 px-1.5 text-green-300',
};

export function ChatPlaceholderOverlay({ examples = [], active, staticFallback }) {
    const rootRef = useRef(null);
    const reduceMotion = useReducedMotion();

    const canAnimate = active && !reduceMotion && examples.length > 0;
    useTypewriterPlaceholder({ rootRef, examples, enabled: canAnimate });

    if (!active) return null;

    // Reduced motion (or no project data yet): one honest static line.
    if (!canAnimate) {
        return (
            <div className="pointer-events-none absolute inset-0 select-none px-4 py-3 text-[15px] text-muted-foreground/70">
                {examples[0]
                    ? examples[0].map((seg, i) => (
                        <span key={i} className={seg.kind ? chipClass[seg.kind] : undefined}>{seg.text}</span>
                    ))
                    : staticFallback}
            </div>
        );
    }

    // The hook writes textContent into these spans, so their count is fixed and
    // their styling is per-segment. Segment 1 is the chip slot.
    return (
        <div
            ref={rootRef}
            aria-hidden="true"
            data-testid="chat-placeholder-typewriter"
            className="pointer-events-none absolute inset-0 select-none truncate px-4 py-3 text-[15px] text-muted-foreground/70"
        >
            {Array.from({ length: SEGMENT_COUNT }, (_, i) => (
                <span
                    key={i}
                    data-seg={i}
                    style={{ display: 'none' }}
                    className={cn(i === 1 && 'rounded-md border px-1.5', i === 1 && chipClass.file)}
                />
            ))}
            <span className="forge-caret ml-px text-primary">▍</span>
        </div>
    );
}

export default ChatPlaceholderOverlay;
